import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Box,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  HelpOutline,
  People as PeopleIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/auth/useAuth';
import { useAuthContext } from '../../contexts/AuthContext';
import NavbarAccountSelector from './NavbarAccountSelector';
import { NavbarImpersonationLock } from './NavbarImpersonationLock';
import { openZendeskFromNavbar } from '../../services/zendesk';
import { useTrackUserEvent } from '../../hooks/api/useTrackUserEvent';

// Navigation items configuration
const NAV_ITEMS = [
  {
    id: 'help',
    icon: HelpOutline,
    labelKey: 'general.help',
    action: 'zendesk-widget' as const,
    variant: 'default' as const,
  },
  {
    id: 'community',
    icon: PeopleIcon,
    labelKey: 'general.community',
    action: 'zendesk-sso' as const,
    url: 'https://help.jst.scifree.se/hc/en-gb/community/topics',
    variant: 'link' as const,
  },
  {
    id: 'logout',
    icon: LogoutIcon,
    labelKey: 'general.signOut',
    action: 'logout' as const,
    variant: 'secondary' as const,
  },
];

interface NavButtonProps {
  item: (typeof NAV_ITEMS)[number];
  onLogout: () => void;
}

const NavButton: React.FC<NavButtonProps> = ({ item, onLogout }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { account } = useAuthContext();
  const { mutate: trackUserEvent } = useTrackUserEvent();

  const handleClick = () => {
    if (item.action === 'logout') {
      onLogout();
      return;
    }

    if (item.action === 'zendesk-widget') {
      openZendeskFromNavbar();
      trackUserEvent({
        slug: account,
        eventCode: 'c',
        payload: { on: 'zendesk-widget' },
      });
      return;
    }

    if (item.action === 'zendesk-sso') {
      window.open(
        `/auth/zendesk-sso?return_to=${encodeURIComponent(item.url)}`,
        '_blank',
        'noopener,noreferrer'
      );
      trackUserEvent({
        slug: account,
        eventCode: 'c',
        payload: { on: 'community' },
      });
    }
  };

  const label = t(item.labelKey);
  const opensInNewTab = item.action === 'zendesk-sso';
  const ariaLabel = opensInNewTab
    ? `${label} - ${t('general.opensInNewTab')}`
    : label;

  const linkProps = {
    variant: item.variant,
    startIcon: <item.icon />,
    'aria-label': ariaLabel,
    onClick: handleClick,
  };

  const button = (
    <Button
      {...linkProps}
      sx={{
        minWidth: 'unset',
        px: { xs: 5, sm: 8, md: 10 },
        py: { xs: 2.5, sm: 3 },
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '.MuiButton-startIcon': {
          mr: { xs: 0, sm: 0.5, md: 1 },
          ml: 0,
        },
        // Hide text on small screens
        '.button-text': {
          display: { xs: 'none', sm: 'none', md: 'inline' },
        },
        // Responsive font size
        '@media (max-width: 600px)': {
          fontSize: theme.typography.body2.fontSize,
        },
      }}
    >
      <span className="button-text">{label}</span>
    </Button>
  );

  if (!isMobile) {
    return button;
  }

  return <Tooltip title={label}>{button}</Tooltip>;
};

interface NavbarProps {
  onDrawerToggle: React.MouseEventHandler<HTMLElement>;
}

const Navbar: React.FC<NavbarProps> = ({ onDrawerToggle }) => {
  const location = useLocation();
  const { loggedInAccount, isImpersonating } = useAuthContext();
  const { logout } = useAuth();
  const theme = useTheme();
  // Hide account selector on specific pages
  const shouldHideAccountSelector = location.pathname === '/accounts';

  const handleLogout = () => {
    localStorage.clear();
    logout();
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background:
          (theme as any).header?.background || theme.palette.background.paper,
        color: (theme as any).header?.color || theme.palette.text.primary,
        px: { xs: 2, md: 12 },
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 56, sm: 60, md: 64 },
          gap: { xs: 2, sm: 3 },
          py: { xs: 0.5, sm: 0.75 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            gap: 2,
          }}
        >
          {/* Mobile menu button */}
          <IconButton
            color="inherit"
            aria-label="Open navigation menu"
            aria-expanded="false"
            aria-controls="mobile-navigation"
            onClick={onDrawerToggle}
            size="small"
            sx={{
              display: { xs: 'block', md: 'none' },
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Account selector for admin */}
          {loggedInAccount === 'admin' && !shouldHideAccountSelector && (
            <Stack direction="row" spacing={2} sx={{ flex: { xs: 1, md: 0 } }}>
              <NavbarAccountSelector />
              {isImpersonating && <NavbarImpersonationLock />}
            </Stack>
          )}

          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Navigation items */}
          <Stack
            component="nav"
            direction="row"
            spacing={{ xs: 2, sm: 3, md: 4 }}
            alignItems="center"
            aria-label="Top navigation"
          >
            {NAV_ITEMS.map((item) => (
              <NavButton key={item.id} item={item} onLogout={handleLogout} />
            ))}
          </Stack>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
